// frontend/src/components/dashboard/SettingsManagement.js
import React, { useState, useEffect } from 'react';
import {
  Box, 
  Paper, 
  TextField, 
  Button, 
  Typography, 
  Select, 
  MenuItem, 
  FormControl,
  InputLabel, 
  Alert, 
  CircularProgress
} from '@mui/material';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import axiosInstance from '../../utils/axios';
import { useTranslation } from 'react-i18next';
import { languages } from '../LanguageSwitcher';
import ImageUploadPreview from './ImageUploadPreview';
import { currencies } from '../constants/currencies';
import { updateFavicon } from '../../utils/favicon';
import TabsSettings from './TabsSettings';

export default function SettingsManagement() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
    headerImage: '',
    logoImage: '',
    promoText: '',
    headerTitle: '',
    headerSubtitle: '',
    defaultLanguage: 'en',
    currency: {
        code: 'USD',
        symbol: '$',
        position: 'left'
    }
});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tabSettingsOpen, setTabSettingsOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  

  useEffect(() => {
      fetchSettings();
      fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
      try {
          const response = await axiosInstance.get('/auth/profile');
          setCurrentUser(response.data);
      } catch (error) {
          console.error('Error fetching user:', error);
      }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
        const response = await axiosInstance.get('/settings');
        setSettings({
            headerImage: response.data?.headerImage || '',
            logoImage: response.data?.logoImage || '',
            promoText: response.data?.promoText || '',
            headerTitle: response.data?.headerTitle || '',
            headerSubtitle: response.data?.headerSubtitle || '',
            defaultLanguage: response.data?.defaultLanguage || 'en',
            currency: {
                code: response.data?.currency?.code || 'USD',
                position: response.data?.currency?.position || 'left',
                symbol: response.data?.currency?.symbol || '$'
            }
        });
    } catch (error) {
        console.error('Error:', error);
        setError('Error loading settings');
    } finally {
        setLoading(false);
    }
};

if (loading) {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
        </Box>
    );
}

  const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      setError(null);
      setSuccess(null);

      try {
          await axiosInstance.patch('/settings', settings);
          if (settings.logoImage) {
              updateFavicon(settings.logoImage);
          }
          if (settings.headerTitle) {
              document.title = settings.headerTitle;
          }
          setSuccess(t('settings.savedSuccessfully'));
          window.location.reload();
      } catch (error) {
          setError(t('settings.errorSaving'));
          console.error('Error updating settings:', error);
      } finally {
          setLoading(false);
      }
  };

  if (loading) {
      return (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
          </Box>
      );
  }

  return (
      <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
              {t('settings.crmSettings')}
          </Typography>

          {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
              </Alert>
          )}

          {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                  {success}
              </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <ImageUploadPreview
                  width={1200}
                  height={450}
                  onImageSelect={(img) => setSettings({...settings, headerImage: img})}
                  currentImage={settings.headerImage}
              />

              <ImageUploadPreview
                  width={200}
                  height={200}
                  onImageSelect={(img) => setSettings({...settings, logoImage: img})}
                  currentImage={settings.logoImage}
                  isLogo
              />

              <TextField
                  fullWidth
                  label={t('settings.promoText')}
                  value={settings.promoText}
                  onChange={(e) => setSettings({ ...settings, promoText: e.target.value })}
                  margin="normal"
                  multiline
                  rows={2}
              />

              <TextField
                  fullWidth
                  label={t('settings.headerTitle')}
                  value={settings.headerTitle}
                  onChange={(e) => setSettings({ ...settings, headerTitle: e.target.value })}
                  margin="normal"
              />

              <TextField
                  fullWidth
                  label={t('settings.headerSubtitle')}
                  value={settings.headerSubtitle}
                  onChange={(e) => setSettings({ ...settings, headerSubtitle: e.target.value })}
                  margin="normal"
              />

              <FormControl fullWidth margin="normal">
                  <InputLabel>{t('settings.defaultLanguage')}</InputLabel>
                  <Select
                      value={settings.defaultLanguage}
                      onChange={(e) => setSettings({ ...settings, defaultLanguage: e.target.value })}
                      label={t('settings.defaultLanguage')}
                  >
                      {languages.map((lang) => (
                          <MenuItem
                              key={lang.code}
                              value={lang.code}
                              sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                              }}
                          >
                              <span style={{ fontSize: '1.2em' }}>{lang.flag}</span>
                              <span>{lang.name}</span>
                          </MenuItem>
                      ))}
                  </Select>
              </FormControl>

              <FormControl fullWidth margin="normal">
    <InputLabel>{t('settings.currency')}</InputLabel>
    <Select
        value={settings?.currency?.code || 'USD'} 
        onChange={(e) => setSettings({
            ...settings,
            currency: {
                ...settings?.currency,
                code: e.target.value,
                position: currencies.find(c => c.code === e.target.value)?.position || 'left'
            }
        })}
        label={t('settings.currency')}
    >
        {currencies.map((currency) => (
            <MenuItem key={currency.code} value={currency.code}>
                {currency.name} ({currency.symbol})
            </MenuItem>
        ))}
    </Select>
</FormControl>

<FormControl fullWidth margin="normal">
    <InputLabel>{t('settings.currencyPosition')}</InputLabel>
    <Select
        value={settings?.currency?.position || 'left'}
        onChange={(e) => setSettings({
            ...settings,
            currency: { ...settings?.currency, position: e.target.value }
        })}
        label={t('settings.currencyPosition')}
    >
        <MenuItem value="left">{t('settings.currencyLeft')}</MenuItem>
        <MenuItem value="right">{t('settings.currencyRight')}</MenuItem>
    </Select>
</FormControl>

              {(currentUser?.role === 'admin' || currentUser?.role === 'superadmin') && (
                  <Button
                      onClick={() => setTabSettingsOpen(true)}
                      variant="outlined"
                      startIcon={<ViewWeekIcon />}
                      sx={{ mt: 3, mb: 2 }}
                      fullWidth
                  >
                      {t('settings.configureTabs')}
                  </Button>
              )}

              <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ mt: 2 }}
                  fullWidth
              >
                  {loading ? t('common.saving') : t('common.savechanges')}
              </Button>
          </Box>

          <TabsSettings
              open={tabSettingsOpen}
              onClose={() => setTabSettingsOpen(false)}
              isAdmin={['admin', 'superadmin'].includes(currentUser?.role)}
          />
      </Paper>
  );
}